import styles from './page-bid-request.module.scss';
import { useEffect, useState, useRef, FC, SetStateAction, ReactNode } from 'react';
import BackButton from '../../components/shared/BackButton/BackButton';
import { RouteComponentProps } from 'react-router-dom';
import Collapsible from '../../components/shared/Collapsible';
import StyledCheckbox from '../../components/shared/StyledCheckbox/StyledCheckbox';
import { ServiceSupplier } from '../../services/service-supplier';
import { ServiceQuotation } from '../../services/service-quotation';
import TooltipContainer from '../../components/shared/TooltipContainer/TooltipContainer';
import BlockIcon from '@material-ui/icons/Block';
import HelpIcon from '@material-ui/icons/Help';
import SupplierBlockedReasons from '../app-supplier-database/supplier-blocked-reasons';
import { Member, Supplier } from '../../models/supplier.model';
import AccessTimeIcon from '@material-ui/icons/AccessTime';
import DatePicker from '../../components/shared/DatePicker/DatePicker';
import TitleWrapper from '../../components/shared/TitleWrapper/TitleWrapper';
import Editor from '../../components/shared/Editor/Editor';
import BtButton from '../bt-button/bt-button';
import { ReactComponent as ClipIcon } from '../../assets/svg/clip.svg';
import { OptionType, OptionTypeWithRequired } from '../../models/global.model';
import { NotificationManager } from "react-notifications";
import moment from 'moment';
import { DemandType } from '../../models/demand.model';
import { PriceDetailType } from '../../models/priceType.model';
import DraggableDropdown from '../../components/shared/DraggableDropdown/draggable-dropdown';
import AddCircle from '@material-ui/icons/AddCircle';
import { CustomLink } from '../../components/routes';
import appState from '../../state/AppStateContainer';
import { ServiceProjects } from '../../services/service-projects';
import AppLayout from '../../components/layout/app-layout/app-layout';
import _ from 'lodash';
import EmailPreview from '../../components/shared/EmailPreview/EmailPreview';
import ScrollToTop from '../scroll-to-top/scroll-to-top';
import { FileScope } from '../../util/appEnum';
import SupplierMemberContainer from '../page-quotation/ContactInfoForm/SupplierMemberContainer/SupplierMemberContainer';
import AddCircleIcon from '@material-ui/icons/AddCircle';
import axios from 'axios';
import FileUpload, { ExistingFile, NewFile } from '../../components/shared/FileUpload/FileUpload';
import Modal from '../../components/shared/Modal/Modal';
import { Loading } from '../../components/shared';
import * as Sentry from "@sentry/react";
import { BidIntentStatus } from '../page-quotation/BidIntentControls/BidIntentControls';
import Badge from '../../components/shared/Badge/Badge';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import HighlightOffIcon from '@material-ui/icons/HighlightOff';
import { ReactComponent as CircleDollarIcon } from '../../assets/svg/circleDollarIcon.svg';
import { useTranslation, Trans } from 'react-i18next';
import FileArea from '../page-files/FileArea';
import serviceFiles from '../../services/service-files';
import ServiceBidRequestDraft, { BidRequestDraft } from '../../services/service-bid-request-draft';
import { ReactComponent as InfoIcon } from '../../assets/svg/InfoIconLight.svg';
import { ReactComponent as TriangleWarningIcon } from '../../assets/svg/triangleWarningIcon.svg';
import hasNoContactPersons from '../../util/supplierContacts';
import { formatAmount } from '../../util';
import {
  BidReminderChoice,
  BidReminderPreset,
  REMINDER_OPTIONS,
  fromIsoDay,
  isPresetSelectable,
  isSelectableReminderDay,
  presetReminderDay,
  reminderWindow,
  resolveReminderDay,
  toIsoDay,
  toReminderPayload,
} from '../../util/bidRequestReminder';

type Params = {
  areaSlug: string;
  project: string;
  version: string;
};

type RouteTypes = RouteComponentProps<Params, Record<string, unknown>>;

type Props = {
  history: RouteTypes['history'];
  match: RouteTypes['match'];
  currentProject: {
    _id: string,
    name: string,
    slug: string,
    area: {
      areaId: { slug: string, name: string, _id: string },
      suppliers: {
        supplierId: { _id: string }
      }[],
      answersLatestBy?: Date,
      publishedAt?: Date,
    }[]
  },
};

type SupplierContactWithState = Member & {
  isRemoved?: boolean,
  isOnEditingMode?: boolean,
  errorMessages?: Omit<Member, '_id'>,
  isNew?: boolean,
  isSelected?: boolean,
  intentStatus?: {
    intent: string,
    declaredAt: string,
    declaredBy?: { _id: string, name: string },
    reasonDeclined: string,
  },
  supplierProfile?: SupplierProfile,
}

const newContact = (supplierId: string) => ({
  _id: `new_${Date.now()}`,
  name: '',
  email: '',
  phone: '',
  title: '',
  supplierId,
  isRemoved: false,
  isOnEditingMode: true,
  isNew: true,
  isSelected: false,
});

type DraftDecision = 'DRAFT' | 'NEW' | 'UNDECIDED';

type SupplierProfile = {
  name?: string;
  areas?: Array<any>;
  places?: Array<string>;
  contacts?: Array<{
    _id: string;
    name: string;
    email: string;
    title: string;
    phone: string;
  }>;
  otherEmailAddresses?: Array<{
    email: string;
    redirectTo?: string;
    comment?: string;
  }>;
};

type Quotation = {
  _id: string;
  refId?: string;
  __v?: number;
  quoteVersionNo?: number;
  refVersion?: number;
  quotationDate?: string;
  fileAreaBidRequestFiles?: string;
  suppliers: Array<{
    supplierId: string;
    supplierMemberId: string;
    intentStatus?: {
      intent: string;
      declaredAt: string;
      declaredBy?: { _id: string; name: string };
      reasonDeclined: string;
    };
  }>;
  supplierBids?: Array<{
    supplierId: string;
    version: number;
    priceDetails?: Array<{
      priceType: string;
      price: number;
    }>;
  }>;
  supplierMembers?: Array<{
    supplierId: string;
    supplierMemberId: string;
  }>;
  emailText?: string;
  quotationExpiryDate: string;
  content: string;
  attachments: Array<{
    fileId?: string;
    mimeType?: string;
  }>;
  demands: DemandType[];
  priceDetails: PriceDetailType[];
};

const PageBidRequest: FC<Props> = ({ history, match, currentProject }) => {
  const [t] = useTranslation('projects', { keyPrefix: 'bidRequest' });
  // The publish warning is the same copy the purchase list shows on its "Publicera förfrågan" button.
  const [tPublish] = useTranslation('projects', { keyPrefix: 'purchaseList.publish' });
  const area = currentProject.area.find(area => area.areaId.slug === match.params.areaSlug);
  const answersLatestBy = area?.answersLatestBy;
  // Publishing is decided by which submit button is used. An already-published request stays
  // published, so it only offers "send new version and publish" — unpublishing lives on the
  // purchase list.
  const isPublished = !!area?.publishedAt;
  // Unique token per page-open so each session gets its own FileArea root.
  // Accepting a draft swaps in the draft's root; discarding keeps this fresh root.
  const fileAreaSessionToken = useRef(Date.now().toString());

  const [projectName, setProjectName] = useState("");
  const [areaName, setAreaName] = useState<string | undefined>("");
  const [areSuppliersOpen, setAreSuppliersOpen] = useState(true);
  const [isMainFormOpen, setIsMainFormOpen] = useState(true);
  const [suppliers, setSuppliers] = useState<Supplier[]>([]);
  const [expirationDate, setExpirationDate] = useState<Date>(answersLatestBy ? new Date(answersLatestBy) : moment().add(1, 'day').endOf('day').toDate());
  const [infoText, setInfoText] = useState('');
  const [files, setFiles] = useState<ExistingFile[]>([]);
  const [newFiles, setNewFiles] = useState<NewFile[]>([]);
  const [demandOptions, setDemandOptions] = useState<OptionType[]>([]);
  const [priceTypeOptions, setPriceTypeOptions] = useState<OptionType[]>([]);
  const [priceTypeUnits, setPriceTypeUnits] = useState<OptionType[]>([]);
  const [demands, setDemands] = useState<OptionTypeWithRequired[]>([]);
  const [priceTypes, setPriceTypes] = useState<OptionTypeWithRequired[]>([]);
  const [isEmailViewerOpen, setIsEmailViewerOpen] = useState(true);
  const [isReminderOpen, setIsReminderOpen] = useState(true);
  const [reminder, setReminder] = useState<BidReminderChoice>({ preset: 'none' });
  const [senderMsg, setSenderMsg] = useState('');
  const [latestVersionNo, setLatestVersionNo] = useState(-1);
  const [publishLoader, setPublishLoader] = useState(false);
  const [notPublishLoader, setNotPublishLoader] = useState(false);
  const [fileScopeQuotationId, setFileScopeQuotationId] = useState(""); // quotationId of source quotation, to be able to access files
  const [fileScopeQuotationVersion, setFileScopeQuotationVersion] = useState(-1); // version of source quotation, to be able to access files
  const [supplierContacts, setSupplierContacts] = useState<SupplierContactWithState[]>([]);
  const [quotation, setQuotation] = useState<Quotation>();
  const [showDraftModal, setShowDraftModal] = useState(false);
  const [showPublishModal, setShowPublishModal] = useState(false);
  const [draftDecision, setDraftDecision] = useState<DraftDecision>('UNDECIDED');
  const [isOldDraft, setIsOldDraft] = useState(false);
  const [isQuotationLoaded, setIsQuotationLoaded] = useState(false);
  const [initialLoadingCompleted, setInitialLoadingCompleted] = useState(false);
  const [infoTextHasLoaded, setInfoTextHasLoaded] = useState(false);
  const [senderMsgHasLoaded, setSenderMsgHasLoaded] = useState(false);
  const [versionForDraft, setVersionForDraft] = useState(-1);
  const [fileAreaPath, setFileAreaPath] = useState('/');
  const [fileAreaRoot, setFileAreaRoot] = useState<string | null>(null);
  const [fileAreaUploading, setFileAreaUploading] = useState(false);

  const [draft, setDraft] = useState<BidRequestDraft | null>(null);
  const lastKnownUpdatedAtRef = useRef<string | null>(null);
  const saveDraftFnRef = useRef<() => void>();
  const debouncedSaveDraft = useRef(_.debounce(() => saveDraftFnRef.current?.(), 1500)).current;

  const emptySupplierProfile = (): SupplierProfile => ({
    areas: [],
    places: [],
    contacts: [],
    otherEmailAddresses: [],
  });

  const fetchQuotation = async () => {
    const query = history.location.search;
    const urlParams = new URLSearchParams(query);
    const copySource = urlParams.get('source');
    const [copySourceArea, copySourceVersion] = copySource ? copySource.split(":") : [null, null];
    let selectedContacts: { supplierId: string, _id: string }[] = [];
    let apiQuotation: Quotation | undefined;
    let fetchedVersionForDraft = -1;
    try {
      let quotationPromise;
      const requestedVersion = match.params.version == "new" ? "new" : parseInt(match.params.version) - 1;
      if (requestedVersion !== undefined) {
        quotationPromise = ServiceQuotation.getAllQuotations(
          match.params.project,
          match.params.areaSlug,
          requestedVersion
        ).catch((error) => {
          if (requestedVersion === "new" && axios.isAxiosError(error) && error.response?.status === 404 && error.response?.data?.error === "error.quotationNotFound") {
            return [];
          } else {
            throw error;
          }
        });
      }

      let resolvedQuotations;
      if (copySource) {
        const copySourcePromise = ServiceQuotation.getAllQuotations(
          match.params.project,
          copySourceArea,
          parseInt(copySourceVersion!) - 1
        );

        resolvedQuotations = await Promise.all([quotationPromise, copySourcePromise]);
      } else {
        resolvedQuotations = [await quotationPromise];
      }

      try {
        const quotations = resolvedQuotations[0];
        const possibleApiQuotation = Array.isArray(quotations) ? quotations[0] : quotations;
        if (possibleApiQuotation) {
          fetchedVersionForDraft = possibleApiQuotation.quoteVersionNo ?? possibleApiQuotation.__v ?? -1;
          setVersionForDraft(fetchedVersionForDraft);
        }

        if (copySource) {
          const quotations = resolvedQuotations[1];
          const quotationsArray = Array.isArray(quotations) ? quotations : [quotations];
          const copySourceVersionNum = parseInt(copySourceVersion!);
          apiQuotation = (!isNaN(copySourceVersionNum))
            ? quotationsArray.find((q: Quotation) => q.quoteVersionNo === copySourceVersionNum - 1)
            : undefined;
        } else {
          apiQuotation = possibleApiQuotation;
        }
      } catch (error) {
        console.error('Error fetching quotations:', error);
        if (axios.isAxiosError(error) && error.response?.status === 404 && error.response?.data?.error === "error.versionNotFound" && !copySource) {
          const quotations = await ServiceQuotation.getAllQuotations(
            match.params.project,
            match.params.areaSlug
          );
          apiQuotation = Array.isArray(quotations) ? quotations[0] : quotations;
        } else {
          throw error;
        }
      }
      setQuotation(apiQuotation as Quotation);

      if (apiQuotation) {
        selectedContacts = _.uniqBy(apiQuotation.supplierMembers, 'supplierMemberId').map(supplier => ({
          supplierId: supplier.supplierId.toString(),
          _id: supplier.supplierMemberId.toString()
        }));

        setSenderMsg(apiQuotation.emailText || "");
        if (apiQuotation.quotationExpiryDate) {
          setExpirationDate(new Date(apiQuotation.quotationExpiryDate));
        }
        setInfoText(apiQuotation.content);
        setFiles(apiQuotation.attachments?.map((file: any) => ({ ...file, file: file.fileId!, mimeType: file.mimeType! })) || []);
        filterAndSetDemands(apiQuotation);
        filterAndSetPriceTypes(apiQuotation);
        setFileScopeQuotationId(apiQuotation.refId ?? apiQuotation._id ?? "");
        setFileScopeQuotationVersion(apiQuotation.quoteVersionNo ?? apiQuotation.__v ?? -1);
      } else {
        setAreSuppliersOpen(true);
      }

      const area = currentProject.area.find(area => area.areaId.slug === match.params.areaSlug);

      let latestQuoteVersionNo = apiQuotation?.quoteVersionNo ?? -1;
      if ((match.params.version !== "new" || copySource) && apiQuotation) {
        try {
          const latestData = await ServiceQuotation.getAllQuotations(
            match.params.project,
            match.params.areaSlug,
            "new"
          );
          const latest = Array.isArray(latestData) ? latestData[0] : latestData;
          if (latest?.quoteVersionNo != null) {
            latestQuoteVersionNo = latest.quoteVersionNo;
          } else {
            latestQuoteVersionNo = -1;
          }
        } catch (e) {
          latestQuoteVersionNo = -1;
        }
      }
      // The new version is always numbered from the LATEST version, regardless
      // of which source version this request is based on — the preview must
      // use the same index the send path will (projectQuotation numbers the
      // new snapshot from the version count, not from the source).
      setLatestVersionNo(latestQuoteVersionNo);
      const newVersionNumber = latestQuoteVersionNo + 2;
      const sourceVersion = match.params.version === "new" ? "new" : (apiQuotation?.quoteVersionNo ?? 0) + 1;
      const newFileAreaRoot = `/bid-request-files/${currentProject._id}/${area?.areaId?._id}/${newVersionNumber}-from-${sourceVersion}/${fileAreaSessionToken.current}`;

      if (apiQuotation?.fileAreaBidRequestFiles) {
        try {
          const existing = await serviceFiles.listFiles(newFileAreaRoot, '/');
          if (!existing?.children?.length) {
            await serviceFiles.copyFileArea(apiQuotation.fileAreaBidRequestFiles, newFileAreaRoot, { ttl: true });
          }
        } catch (e) {
          console.error('Error initializing file area:', e);
        }
      }

      setFileAreaRoot(newFileAreaRoot);
      const areaSuppliers = area?.suppliers?.map(supplier => supplier.supplierId._id);

      await Promise.all([
        fetchDemands(),
        fetchPriceTypes(),
        fetchPriceUnits(),
      ]);

      const suppliersWithMembers: { _id: Supplier, members: Member[] }[] = await ServiceSupplier.getSuppliersMembers(areaSuppliers) || [];
      let tempSupplierContacts: SupplierContactWithState[] = [];

      const suppliersWithProfiles = await Promise.all(
        suppliersWithMembers.map(async ({ _id, members }) => {
          let supplierProfile: SupplierProfile | null = null;
          if (_id.organizationNumber) {
            try {
              const supplierData = await ServiceSupplier.fetchSupplier({
                searchFilter: _id.organizationNumber,
                mini: true
              });
              supplierProfile = supplierData.data?.[0]?.supplierProfile ?? emptySupplierProfile();
            } catch (error) {
              if (!axios.isAxiosError(error) || error.response?.status !== 404) {
                console.error('Error fetching supplier profile:', error);
              }
              supplierProfile = emptySupplierProfile();
            }
          }

          tempSupplierContacts = [...tempSupplierContacts, ...members.map(m => ({
            ...m,
            isSelected: false,
            supplierProfile: supplierProfile || undefined
          }))];
          const quotationSupplier = apiQuotation?.suppliers?.find(s => s.supplierId === _id._id);
          const intentStatus = quotationSupplier?.intentStatus ? {
            intent: quotationSupplier.intentStatus.intent,
            declaredAt: quotationSupplier.intentStatus.declaredAt,
            declaredBy: quotationSupplier.intentStatus.declaredBy,
            reasonDeclined: quotationSupplier.intentStatus.reasonDeclined,
          } : undefined;

          return {
            _id: _id._id,
            name: _id.name,
            isBlacklist: _id.isBlacklist,
            blacklistReason: _id.blacklistReason,
            organizationNumber: _id.organizationNumber,
            intentStatus
          };
        })
      );

      setSupplierContacts(tempSupplierContacts);


      setSuppliers(suppliersWithProfiles);


      if (selectedContacts?.length > 0) {
        selectedContacts.forEach(selectedContact => {
          const contactIndex = tempSupplierContacts.findIndex(_contact => _contact._id === selectedContact._id && _contact.supplierId === selectedContact.supplierId);
          if (contactIndex !== -1) {
            tempSupplierContacts[contactIndex].isSelected = true;
          }
        });
      }

      setProjectName(currentProject?.name);
      setAreaName(area?.areaId?.name);

      try {
        const fetchedDraft = await ServiceBidRequestDraft.getDraft(
          currentProject._id,
          area!.areaId._id
        );
        setDraft(fetchedDraft);
        lastKnownUpdatedAtRef.current = fetchedDraft?.updatedAt ?? null;
      } catch (e) {
        // Non-fatal: draft fetch failing should not block the form
        console.error('Error fetching bid request draft:', e);
      }

      setIsQuotationLoaded(true);
      setInitialLoadingCompleted(true);
    } catch (error) {
      if (axios.isAxiosError(error) && error?.response?.status === 404 && error?.response?.data?.error === "error.quotationNotFound") {
        setAreSuppliersOpen(true);
      } else {
        NotificationManager.error(
          '' + error,
          t('notification.error.submit.unexpected', 'Oväntat fel'),
        );
        throw error;
      }
    }
  };

  // Strip the non-serialisable / re-fetched fields from contacts before comparing or saving
  const stripContactProfile = (contacts: SupplierContactWithState[]) =>
    contacts.map(({ supplierProfile: _p, ...rest }) => rest);

  const checkIfDraftIsValid = (draft: BidRequestDraft | null) => {
    if (!draft?.data) return false;

    const stateObject = {
      expirationDate,
      infoText,
      files,
      priceTypeUnits,
      demands,
      priceTypes,
      senderMsg,
      fileScopeQuotationId,
      fileScopeQuotationVersion,
      supplierContacts: stripContactProfile(supplierContacts),
      fileAreaRoot,
      reminder,
    };

    const draftData = {
      expirationDate: draft.data.expirationDate,
      infoText: draft.data.infoText,
      files: draft.data.files,
      priceTypeUnits: draft.data.priceTypeUnits,
      demands: draft.data.demands,
      priceTypes: draft.data.priceTypes,
      senderMsg: draft.data.senderMsg,
      fileScopeQuotationId: draft.data.fileScopeQuotationId,
      fileScopeQuotationVersion: draft.data.fileScopeQuotationVersion,
      supplierContacts: draft.data.supplierContacts,
      fileAreaRoot: draft.data.fileAreaRoot,
      reminder: draft.data.reminder ?? { preset: 'none' },
    };

    return JSON.stringify(draftData) !== JSON.stringify(stateObject);
  }

  useEffect(() => {
    fetchQuotation();
  }, []);

  const setStateOfDraftModal = () => {
    if (isQuotationLoaded && draftDecision === 'UNDECIDED') {
      const urlDraftDecision = new URLSearchParams(history.location.search).get('draftDecision');
      if (urlDraftDecision === 'use' && draft?.data) {
        onDraftAccept();
        return;
      }

      if (match.params.version === 'new' && checkIfDraftIsValid(draft)) {
        if (quotation && moment((quotation as Quotation)?.quotationDate).isAfter(moment(draft?.updatedAt))) {
          setIsOldDraft(true);
        }
        setShowDraftModal(true);
        return;
      }

      setDraftDecision('NEW');
    }
  }

  useEffect(() => {
    if (!isQuotationLoaded || draftDecision !== 'UNDECIDED') return;
    if (!infoTextHasLoaded) return;
    if (!senderMsgHasLoaded) return;
    setStateOfDraftModal();
    setInitialLoadingCompleted(true);
  }, [infoTextHasLoaded, senderMsgHasLoaded]);

  const goBack = () => {
    const url = match.url.split('/');
    url.splice(5);
    history.push(url.join('/'));
  };

  const fetchDemands = async () => {
    let allDemands = await ServiceQuotation.getDemands();
    allDemands = allDemands.map((d: { _id: string, name: string }) => ({ label: d.name, value: d._id }));
    setDemandOptions(allDemands);
  }

  const fetchPriceTypes = async () => {
    let allPriceTypes = await ServiceQuotation.getPriceTypes();
    allPriceTypes = allPriceTypes.map((d: { _id: string, name: string }) => ({ label: d.name, value: d._id }));
    setPriceTypeOptions(allPriceTypes);
  }

  const fetchPriceUnits = async () => {
    let allUnits = await ServiceQuotation.getPriceUnits();
    allUnits = allUnits
      .map((x: { _id: string, name: string }) => x.name)
      .sort((a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase()))
      .map((s: string) => ({ label: s, value: s }));
    setPriceTypeUnits(allUnits);
  }

  const filterAndSetDemands = (quotation: Quotation) => {
    setDemands((prevState) => {
      let filteredDemands = [...prevState];
      if (quotation?.demands) filteredDemands = quotation?.demands?.map((d: DemandType, index: number) => ({
        label: d?.demandId.name,
        value: "existing" + index,
        required: d?.required
      }));
      return filteredDemands;
    })
  }

  const filterAndSetPriceTypes = (quotation: Quotation) => {
    setPriceTypes((prevState) => {
      let filteredPriceTypes = [...prevState];
      if (quotation?.priceDetails) filteredPriceTypes = quotation?.priceDetails?.map((p: PriceDetailType, index: number) => ({
        label: p?.priceType.name,
        value: "existing" + index,
        required: p?.required,
        unit: p.unit ?? "kr"
      }));
      return filteredPriceTypes;
    })
  }

  useEffect(() => {
    const units = priceTypes.reduce(
      (set, p) => p?.unit ? set.add(p?.unit) : set,
      new Set(priceTypeUnits.map(option => option?.label ?? "kr"))
    );
    setPriceTypeUnits(
      Array.from(units)
        .sort((a: string, b: string) => a.toLowerCase().localeCompare(b.toLowerCase()))
        .map(s => ({ label: s, value: s }))
    );
  }, [priceTypes]);

  saveDraftFnRef.current = async () => {
    if (!isQuotationLoaded || draftDecision === 'UNDECIDED' || !initialLoadingCompleted) return;
    // Skip when local state still matches the last known draft — covers the
    // priceTypes→priceTypeUnits cascade that re-runs right after onDraftAccept
    // and would otherwise queue a redundant PUT of identical data.
    if (draft && !checkIfDraftIsValid(draft)) return;

    const data = {
      expirationDate,
      infoText,
      files,
      priceTypeUnits,
      demands,
      priceTypes,
      senderMsg,
      fileScopeQuotationId,
      fileScopeQuotationVersion,
      supplierContacts: stripContactProfile(supplierContacts),
      fileAreaRoot,
      reminder,
    };

    const result = await ServiceBidRequestDraft.saveDraft(
      currentProject._id,
      area!.areaId._id,
      versionForDraft,
      data,
      lastKnownUpdatedAtRef.current
    );

    if ('error' in result && result.error === 'conflict') {
      // Another user saved while we were editing — warn, then force-save our version
      NotificationManager.warning(
        t(
          'draft.conflictWarning',
          'Utkastet uppdaterades av {{name}} medan du redigerade. Din version sparas nu.',
          { name: result.savedBy }
        ),
        '',
        6000
      );
      // Force-save (omit lastKnownUpdatedAt to bypass conflict check)
      const forcedResult = await ServiceBidRequestDraft.saveDraft(
        currentProject._id,
        area!.areaId._id,
        versionForDraft,
        data,
        null
      );
      if (!('error' in forcedResult)) {
        setDraft(forcedResult);
        lastKnownUpdatedAtRef.current = forcedResult.updatedAt;
      }
    } else if (!('error' in result)) {
      setDraft(result);
      lastKnownUpdatedAtRef.current = result.updatedAt;
    }
  };

  const updateDraftWithLatestInfo = () => debouncedSaveDraft();

  useEffect(() => {
    updateDraftWithLatestInfo();
  }, [
    expirationDate,
    infoText,
    files,
    priceTypeUnits,
    demands,
    priceTypes,
    senderMsg,
    fileScopeQuotationId,
    fileScopeQuotationVersion,
    supplierContacts,
    fileAreaRoot,
    reminder,
  ]);

  // Drop any autosave queued during initial load — the setters that ran while
  // fetching the quotation/draft don't represent user intent. After this fires,
  // subsequent edits trigger fresh debounced saves normally.
  useEffect(() => {
    if (draftDecision === 'UNDECIDED') return;
    debouncedSaveDraft.cancel();
  }, [draftDecision]);

  const renderRecipientsList = () => {
    const toggleMember = (contactId: string) => {
      setSupplierContacts(prevState => {
        const tempContacts = [...prevState];
        const index = tempContacts.findIndex(contact => contact._id === contactId);
        if (tempContacts[index]) {
          tempContacts[index].isSelected = !tempContacts[index].isSelected;
        }
        return tempContacts;
      })
    }

    const onChange = (member: SupplierContactWithState, values: Partial<Member>) => {
      setSupplierContacts(contacts => {
        const tempContacts = [...contacts];
        const contactIndex = tempContacts.findIndex(contact => contact._id === member._id && contact.supplierId === member.supplierId);
        tempContacts[contactIndex] = {
          ...tempContacts[contactIndex],
          ...values,
          errorMessages: {
            name: '',
            email: '',
            title: '',
            phone: '',
          },
        }
        return tempContacts;
      })
    }

    const onEdit = (member: SupplierContactWithState) => {
      setSupplierContacts(oldState => {
        const tempContacts = [...oldState];
        const contactIndex = tempContacts.findIndex(contact => contact._id === member._id && contact.supplierId === member.supplierId);

        tempContacts[contactIndex].isOnEditingMode = true;

        return tempContacts;
      });
    }

    const onRemove = (member: SupplierContactWithState, isRemoved: boolean) => {
      if (!member.isNew) {
        setSupplierContacts(prevState => {
          const tempContacts = [...prevState];

          const contactIndex = tempContacts.findIndex(contact => contact._id === member._id && contact.supplierId === member.supplierId);

          tempContacts[contactIndex].isRemoved = isRemoved;

          if (isRemoved) {
            tempContacts[contactIndex].isSelected = false;
            if (tempContacts.filter(contact => contact.supplierId === member.supplierId && contact.isNew).length === 0 && !tempContacts.find(contact => contact.supplierId === member.supplierId && !contact.isRemoved && !contact.isNew)) {
              tempContacts.push(newContact(member.supplierId!));
            }
          } else if (quotation) {
            const contactWasPartOfTheQuotation = _.uniqBy(quotation.suppliers, 'supplierMemberId')?.find(quotationSupplier => member.supplierId === quotationSupplier.supplierId.toString() && member._id == quotationSupplier.supplierMemberId.toString());
            if (contactWasPartOfTheQuotation) {
              tempContacts[contactIndex].isSelected = true;
            }
          }
          return tempContacts;
        });
      } else {
        setSupplierContacts(contacts => {
          const newContacts = contacts.filter(contact => !(contact._id === member._id && contact.supplierId === member.supplierId));
          if (newContacts.filter(contact => contact.supplierId === member.supplierId && contact.isNew).length === 0 && !contacts.find(contact => contact.supplierId === member.supplierId && !contact.isRemoved && !contact.isNew)) {
            newContacts.push(newContact(member.supplierId!))
          }
          return newContacts
        })
      }
    }

    return (
      <Collapsible
        header={(
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <strong>{t('recipients.header', '{{count}} mottagare', { count: supplierContacts.filter(contact => !!contact.isSelected).length })}</strong>
          </div>
        )}
        isOpen={areSuppliersOpen}
        onChangeCollapse={() => setAreSuppliersOpen(oldState => !oldState)}
      >
        {suppliers.map(({ _id, name, isBlacklist, blacklistReason, intentStatus }) => {
          const hasSubmittedBid = quotation?.supplierBids?.some((bid: { supplierId: string; priceDetails?: Array<{ priceType: string; price: number }> }) =>
            bid.supplierId === _id && Array.isArray(bid.priceDetails)
          );
          // Suppliers with no contact person are listed here rather than hidden, so the request can
          // be built for them and a contact added on the spot. A row the user has only just opened
          // holds no contact yet, so it keeps the warning until they start filling it in.
          const missingContactPersons = hasNoContactPersons(
            supplierContacts.filter(contact =>
              contact.supplierId === _id && !contact.isRemoved && (contact.name || contact.email))
          );
          return (
            <div key={_id} className={`${styles.supplierContainer} ${isBlacklist ? styles.isBlacklisted : {}}`}>
              <div className={styles.supplierNameContainer}>
                {isBlacklist &&
                  <TooltipContainer renderReferenceComponent={(className, ref) =>
                    <BlockIcon innerRef={ref} className={`bid-card-blackList-icon ${styles.blackListIcon} ${className}`} />
                  }>
                    {blacklistReason?.length > 0 && <SupplierBlockedReasons reasons={blacklistReason} />}
                  </TooltipContainer>
                }
                <strong>{name}</strong>
                {missingContactPersons && (
                  <TooltipContainer renderReferenceComponent={(className, ref) => (
                    <div ref={ref} className={className}>
                      <Badge
                        className={styles.missingContactPersonsBadge}
                        backgroundColor="var(--yellow-100)"
                        color="var(--yellow-800)"
                      >
                        <TriangleWarningIcon className={styles.missingContactPersonsIcon} />
                        {t('recipients.badge.missingContactPersons', 'Saknar kontaktpersoner')}
                      </Badge>
                    </div>
                  )}>
                    <div style={{ padding: '8px', fontSize: '14px', fontWeight: 400, color: 'var(--gray-700)', maxWidth: '300px' }}>
                      {t(
                        'recipients.badge.tooltip.missingContactPersons',
                        'Leverantören har ingen kontaktperson. Lägg till en här för att kunna skicka förfrågningsunderlaget till den.'
                      )}
                    </div>
                  </TooltipContainer>
                )}
                {hasSubmittedBid ? (
                  <TooltipContainer
                    renderReferenceComponent={(className, ref) => (
                      <div ref={ref} className={className}>
                        <Badge
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginLeft: '8px',
                            fontSize: '13px',
                            fontWeight: 400,
                            padding: '4px 12px',
                            borderRadius: '4px',
                            textTransform: 'none',
                            height: '28px',
                            backgroundColor: 'var(--green-100)',
                            color: 'var(--green-700)',
                          }}
                        >
                          <CircleDollarIcon style={{ fontSize: 18, color: 'var(--green-700)' }} />
                          {t('recipients.badge.hasSubmittedBid', 'Har lämnat anbud')}
                        </Badge>
                      </div>
                    )}
                  >
                    <div style={{ padding: '8px', fontSize: '14px', fontWeight: 400, color: 'var(--gray-700)' }}>
                      {renderBidPrice(_id)}
                    </div>
                  </TooltipContainer>
                ) : intentStatus?.intent && intentStatus.intent !== 'UNDECIDED' && (
                  <TooltipContainer
                    renderReferenceComponent={(className, ref) => (
                      <div ref={ref} className={className}>
                        <Badge
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            marginLeft: '8px',
                            fontSize: '13px',
                            fontWeight: 400,
                            padding: '4px 12px',
                            borderRadius: '4px',
                            textTransform: 'none',
                            height: '28px',
                          }}
                          backgroundColor={
                            intentStatus.intent === BidIntentStatus.ACCEPTED
                              ? 'var(--green-100)'
                              : intentStatus.intent === BidIntentStatus.DECLINED
                                ? 'var(--red-100)'
                                : ''
                          }
                          color={
                            intentStatus.intent === BidIntentStatus.ACCEPTED
                              ? 'var(--green-700)'
                              : intentStatus.intent === BidIntentStatus.DECLINED
                                ? 'var(--red-700)'
                                : ''
                          }
                        >
                          {intentStatus.intent === BidIntentStatus.ACCEPTED ? (
                            <CheckCircleOutlineIcon style={{ fontSize: 18 }} />
                          ) : (
                            <HighlightOffIcon style={{ fontSize: 18 }} />
                          )}
                          {intentStatus.intent === BidIntentStatus.ACCEPTED
                            ? t('recipients.badge.accepted', 'Avser lämna anbud')
                            : intentStatus.intent === BidIntentStatus.DECLINED
                              ? t('recipients.badge.declined', 'Tackat nej')
                              : ''}
                        </Badge>
                      </div>
                    )}
                  >
                    <div style={{ padding: '8px', fontSize: '14px', fontWeight: 400, color: 'var(--gray-700)' }}>
                      {intentStatus.declaredBy?.name ? t('recipients.badge.tooltip.intentStatus', '{{name}} svarade den {{timestamp}}', {
                        name: intentStatus.declaredBy?.name,
                        timestamp: moment(intentStatus.declaredAt).locale('sv').format('D MMMM YYYY HH:mm'),
                      }) : t('recipients.badge.tooltip.intentStatusUnknownDeclarer', 'Svarade den {{timestamp}}', { 
                        timestamp:  moment(intentStatus.declaredAt).locale('sv').format('D MMMM YYYY HH:mm')
                      })}
                    </div>
                  </TooltipContainer>
                )}
              </div>
              <div className={styles.supplierMembersContainer}>
                {supplierContacts.filter(contact => contact.supplierId === _id).map(member =>
                  <label key={`${member._id}${member.supplierId}`} className={styles.memberContainer}>
                    <StyledCheckbox
                      onChange={() => !member.isRemoved && toggleMember(member._id)}
                      checked={!!member?.isSelected}
                    />
                    <SupplierMemberContainer
                      isSupplierBlocked={isBlacklist}
                      key={`${member._id}${member.supplierId}`}
                      member={member}
                      supplierProfile={member.supplierProfile}
                      errors={member?.errorMessages || { name: '', email: '', title: '', phone: '' }}
                      editMode={member.isOnEditingMode || !!member.isNew}
                      onChange={(values) => onChange(member, values)}
                      onEdit={() => onEdit(member)}
                      onRemove={!(supplierContacts.filter(contact => contact.supplierId === _id && !contact.isRemoved && member.isNew).length === 1 && member.isNew) ? (isRemoved) => onRemove(member, isRemoved) : undefined}
                    />
                  </label>
                )}
                {missingContactPersons && (
                  <div className={styles.missingContactPersonsHint}>
                    <TriangleWarningIcon className={styles.missingContactPersonsIcon} />
                    <span>
                      {t(
                        'recipients.missingContactPersonsHint',
                        'Förfrågningsunderlaget kan inte skickas till den här leverantören förrän den har en kontaktperson.'
                      )}
                    </span>
                  </div>
                )}
                <BtButton
                  color="white"
                  size="xxs"
                  leftIcon={<AddCircleIcon htmlColor="var(--orange-500)" />}
                  style={{ width: 'fit-content' }}
                  onClick={() => setSupplierContacts(oldState => [...oldState, newContact(_id)])}
                >
                  {t('recipients.button.addRecipient', 'Lägg till kontaktperson')}
                </BtButton>
              </div>
            </div>
          );
        })}
      </Collapsible>
    )
  }

  const renderBidPrice = (supplierId: string) => {
    type PriceDetail = {
      priceType: {
        _id: string;
        name: string;
      };
      price: number;
    };

    type SupplierBid = {
      supplierId: string;
      priceDetails?: PriceDetail[];
      version: number;
    };

    const bid = (quotation?.supplierBids as SupplierBid[] | undefined)
      ?.find(bid => bid.supplierId === supplierId);

    if (!bid) return undefined;

    const priceDetails = bid.priceDetails;
    const unitOf = (detail: PriceDetail) =>
      quotation?.priceDetails?.find(p => p.priceType?._id === detail.priceType?._id)?.unit
      ?? t('mainForm.unit.monetary', 'kr');

    return (
      <div>
        <div style={{ fontSize: '14px', fontWeight: 500, color: 'var(--gray-900)' }}>
          {t('recipients.badge.tooltip.bidVersion', 'Leverantören har senast lämnat anbud på version {{version}}.', { version: bid.version + 1 })}
        </div>
        {priceDetails && priceDetails.length > 0 && priceDetails.map((detail, index) => (
          <div key={index} style={{ marginTop: '8px', fontSize: '12px', fontWeight: 400, color: 'var(--gray-700)' }}>
            {detail.priceType.name}: {formatAmount(detail.price)} {unitOf(detail)}
          </div>
        ))}
      </div>
    );
  };

  const renderMainForm = () => {
    const addField = <T extends OptionType>(
      _list: T[],
      setList: (value: SetStateAction<OptionType[]>) => void
    ) => {
      setList((prevState: OptionType[]) => [...prevState, { label: '', value: "new" + Date.now().toString(), required: false, unit: t('mainForm.unit.monetary', 'kr') }]);
    };

    const renderDraggableContainer = ({ title, buttonText, list, options, onChange, rightSideComponent, units }: {
      title: string,
      buttonText: string,
      list: OptionTypeWithRequired[],
      options: OptionType[],
      onChange: (value: SetStateAction<OptionType[]>) => void,
      rightSideComponent?: ReactNode,
      units?: OptionType[],
    }) => {
      return (
        <TitleWrapper title={title} className={styles.titleWrapper}>
          <div style={{ position: 'relative' }}>
            <div style={{ marginLeft: '-32px', marginRight: '-32px' }}>
              {rightSideComponent}
              <DraggableDropdown
                options={options}
                list={list}
                onChange={onChange}
                units={units}
              />
            </div>

            <BtButton class='white' size='xxs' onClick={() => addField(list, onChange)}>
              <AddCircle style={{ fontSize: '16px', fill: 'var(--orange-500)' }} />
              <span>{buttonText}</span>
            </BtButton>
          </div>
        </TitleWrapper>
      )
    }

    const renderProcTips = () => {
      return (
        <div className={styles.procTipsContainer}>
          <HelpIcon className={styles.icon} />
          <CustomLink
            to={`/projects/${match.params.project}/${match.params.areaSlug}/tips`}
            target="_blank"
          >
            <p>{t('mainForm.procurementTipsLink', 'Se upphandlingstips för {{areaName}}', { areaName })}</p>
          </CustomLink>
        </div>
      );
    };

    return (
      <Collapsible
        header={<strong>{t('mainForm.heading', 'Förfrågningsunderlag')}</strong>}
        isOpen={isMainFormOpen}
        onChangeCollapse={() => setIsMainFormOpen(oldState => !oldState)}
        contentContainerClassName={styles.mainForm}
      >
        <div className={styles.expirationDateContainer}>
          <div>
            <AccessTimeIcon style={{ width: '14px', height: '14px' }} />
            <span>{t('mainForm.label.expirationDate', 'Utgångstid:')}</span>
          </div>
          <DatePicker selected={expirationDate} onChange={setExpirationDate} wrapperClassName={styles.datePicker} />
        </div>
        <TitleWrapper title={t('mainForm.label.information', 'INFORMATION')} className={styles.titleWrapper}>
          {isQuotationLoaded && <Editor
            value={infoText}
            onChange={setInfoText}
            wrapperStyle={{ height: '291px' }}
            onLoadContent={() => setInfoTextHasLoaded(true)}
          />}
        </TitleWrapper>
        <TitleWrapper title={t('mainForm.label.files', 'HANDLINGAR')} className={styles.titleWrapper}>
          {fileAreaRoot && <FileArea
            root={fileAreaRoot}
            path={fileAreaPath}
            setPath={(getNewPath) => {
              const newPath = getNewPath(fileAreaPath);
              setFileAreaPath(newPath);
            }}
            isAllowedToUpload={appState.isWriteAccessRole()}
            isAllowedToDownload={appState.isCustomerUser()}
            isAllowedToDelete={appState.isWriteAccessRole()}
            hasTtl
            onUploadingChange={setFileAreaUploading}
            onFilesChanged={updateDraftWithLatestInfo}
          />}
          {files && files.length > 0 && fileAreaRoot ? <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
            <p className={styles.titleSolo}>{t('mainForm.label.oldFiles', 'ÄLDRE HANDLINGAR')}</p>
            <TooltipContainer renderReferenceComponent={(className, ref) => <InfoIcon className={className} ref={ref} />}>
              {t('mainForm.label.oldFilesToolTip', 'Dessa dokument lades till i en tidigare version av Accurator.')}
            </TooltipContainer>
          </div> : null}
          {files && files.length > 0 ? <FileUpload
            existingFiles={files}
            setExistingFiles={setFiles}
            newFiles={newFiles}
            setNewFiles={setNewFiles}
            icon={<ClipIcon style={{ width: '15px', height: '15px', fill: 'var(--gray-700)' }} />}
            scope={FileScope.BidRequestAttachment}
            scopeOptions={{ quotationId: fileScopeQuotationId, versionNumber: fileScopeQuotationVersion }}
            label={t('mainForm.button.addFiles', 'Bifoga handlingar')}
            hasRemoveAllButton
            readOnlyWithRemove={true}
          /> : null}
        </TitleWrapper>

        {renderDraggableContainer({
          title: t('mainForm.label.priceFields', 'PRISFÄLT'),
          buttonText: t('mainForm.button.addPriceField', 'Lägg till prisfält'),
          list: priceTypes,
          options: priceTypeOptions,
          onChange: setPriceTypes,
          rightSideComponent: renderProcTips(),
          units: priceTypeUnits,
        })}

        {renderDraggableContainer({
          title: t('mainForm.label.demands', 'SKALLKRAV'),
          buttonText: t('mainForm.button.addDemand', 'Lägg till skallkrav'),
          list: demands,
          options: demandOptions,
          onChange: setDemands,
        })}
      </Collapsible>
    )
  }

  const renderEmailViewer = () => {
    return (
      <Collapsible
        header={<strong>{t('email.heading', 'E-postmeddelande')}</strong>}
        isOpen={isEmailViewerOpen}
        onChangeCollapse={() => setIsEmailViewerOpen(oldState => !oldState)}
      >
        <div className={styles.emailWrapper}>
          <EmailPreview
            type="bidRequest"
            params={{
              areaName,
              projectName,
              expiryDate: moment(expirationDate).locale('sv').format('YYYY-MM-DD'),
              versionNo: latestVersionNo + 1,
            }}
          >
            {isQuotationLoaded && <Editor
              value={senderMsg}
              onChange={setSenderMsg}
              wrapperStyle={{ height: '400px' }}
              onLoadContent={() => { setSenderMsgHasLoaded(true); }}
            />}
          </EmailPreview>
        </div>
      </Collapsible>
    );
  };

  const renderReminder = () => {
    const today = new Date();
    const presetLabel: Record<BidReminderPreset | 'none', string> = {
      none: t('reminder.option.none', 'Ingen påminnelse'),
      halfway: t('reminder.option.halfway', 'Halvvägs till utgångstiden'),
      sevenDaysBefore: t('reminder.option.sevenDaysBefore', '7 dagar innan'),
      oneDayBefore: t('reminder.option.oneDayBefore', '1 dag innan'),
      custom: t('reminder.option.custom', 'Välj datum'),
    };
    const formatDay = (day: Date) => moment(day).format('D MMMM');
    const subFor = (preset: BidReminderChoice['preset'], day: Date | undefined, selectable: boolean) => {
      if (!selectable) return t('reminder.option.unavailable', 'Går inte att välja');
      if (preset === 'none') return t('reminder.option.noneSub', 'Endast förfrågningsunderlaget skickas');
      if (preset === 'custom') return t('reminder.option.customSub', 'Valfri dag före utgångstiden');
      return day ? formatDay(day) : '';
    };
    const options = REMINDER_OPTIONS.map(preset => {
      const day = presetReminderDay(preset, expirationDate, today);
      const selectable = isPresetSelectable(preset, expirationDate, today);
      return { preset, label: presetLabel[preset], sub: subFor(preset, day, selectable), selectable };
    });
    const reminderDay = resolveReminderDay(reminder, expirationDate, today);
    const summary = reminder.preset === 'none'
      ? presetLabel.none
      : reminderDay
        ? `${presetLabel[reminder.preset]} · ${formatDay(fromIsoDay(reminderDay))}`
        : t('reminder.summary.missingDate', 'Datum saknas');
    const { first, last } = reminderWindow(expirationDate, today);

    return (
      <Collapsible
        header={(
          <div className={styles.reminderHeader}>
            <strong>{t('reminder.heading', 'Påminnelse')}</strong>
            <span>{summary}</span>
          </div>
        )}
        isOpen={isReminderOpen}
        onChangeCollapse={() => setIsReminderOpen(oldState => !oldState)}
      >
        <div className={styles.reminderWrapper}>
          <p>{t('reminder.description', 'Skickas på morgonen till mottagare som inte har lämnat anbud. Mottagare som tackat nej påminns inte. En påminnelse per version.')}</p>
          <div className={styles.reminderOptions}>
            {options.map(option => {
              const optionButton = (
                <button
                  key={option.preset}
                  type="button"
                  aria-pressed={reminder.preset === option.preset}
                  disabled={!option.selectable}
                  className={`${styles.reminderOption} ${reminder.preset === option.preset ? styles.reminderOptionSelected : ''}`}
                  onClick={() => setReminder(previous => ({
                    preset: option.preset,
                    date: option.preset === 'custom' ? previous.date : undefined,
                  }))}
                >
                  <span className={styles.reminderOptionLabel}>{option.label}</span>
                  <span className={styles.reminderOptionSub}>{option.sub}</span>
                </button>
              );
              if (option.selectable) return optionButton;
              return (
                <TooltipContainer
                  key={option.preset}
                  renderReferenceComponent={(className, ref) => (
                    <div ref={ref} className={`${styles.reminderOptionBlocked} ${className}`}>
                      {optionButton}
                    </div>
                  )}
                >
                  <div className={styles.reminderOptionTooltip}>
                    {t('reminder.option.unavailableTooltip', 'Går inte att välja eftersom utgångstiden är för kort.')}
                  </div>
                </TooltipContainer>
              );
            })}
          </div>
          {reminder.preset === 'custom' && (
            <div className={styles.reminderDate}>
              <span>{t('reminder.dateLabel', 'Påminnelsedatum')}</span>
              <DatePicker
                selected={reminder.date ? fromIsoDay(reminder.date) : undefined}
                onChange={(day: Date | null) => setReminder({ preset: 'custom', date: day && isSelectableReminderDay(day, expirationDate, today) ? toIsoDay(day) : undefined })}
                minDate={first}
                maxDate={last}
                isClearable
              />
            </div>
          )}
        </div>
      </Collapsible>
    );
  };

  const handleSubmit = async (shouldPublish: boolean, setLoader: (value: boolean) => void) => {
    const getNewFiles = (array: NewFile[]) => {
      return array.reduce((obj: any, newFile: NewFile) => {
        obj[newFile.key] = ({
          id: newFile.key,
          fileName: newFile.file.name,
          mimeType: newFile.file.type,
          fileObj: newFile.file,
        });
        return obj;
      }, {})
    }

    setLoader(true);
    try {
      const membersToUpdate = supplierContacts?.filter(contact => !contact.isRemoved && contact.isOnEditingMode && !contact.isNew) || [];
      const membersToRemove = supplierContacts?.filter(contact => contact.isRemoved);
      const membersToAdd = supplierContacts?.filter(contact => contact.isNew);
      const selectedMembers = supplierContacts?.filter(contact => contact.isSelected)?.map(contact => ({ supplierId: contact.supplierId, supplierMemberId: contact._id }));
      const area = currentProject.area.find(area => area.areaId.slug === match.params.areaSlug);
      const projectSlug = match.params.project;
      const areaSlug = match.params.areaSlug;
      const reminderPayload = toReminderPayload(reminder, expirationDate, new Date());
      if (reminder.preset !== 'none' && !reminderPayload) {
        NotificationManager.error(
          t('errorMessage.summary', 'Kan inte skicka förfrågan: {{errorMessage}}.', { errorMessage: t('errorMessage.unschedulableReminder', 'påminnelsen saknar ett datum mellan imorgon och dagen före utgångstiden') }),
          t('notification.error.submit.title', 'Kunde inte skicka')
        );
        return;
      }
      const _demands = demands?.filter(l => l?.label !== t('mainForm.placeholder.defaultSelect', 'Välj'))?.map(d => {
        const existing = demandOptions.find(o => o?.label === d?.label);
        return {
          demandId: existing?.value ?? "new",
          name: d?.label,
          isNew: !existing,
          required: d?.required
        };
      });
      const _priceTypes = priceTypes?.filter(l => l?.label !== t('mainForm.placeholder.defaultSelect', 'Välj'))?.map(p => {
        const existing = priceTypeOptions.find(o => o?.label === p?.label);
        return {
          priceType: existing?.value ?? "new",
          name: p?.label,
          isNew: !existing,
          required: p?.required,
          unit: p?.unit,
        };
      });

      const projectQuotation = {
        projectId: currentProject._id,
        areaId: area?.areaId?._id,
        quotationExpiryDate: moment(expirationDate).endOf('day').utc(),
        content: infoText,
        attachments: JSON.stringify(files),
        newFiles: getNewFiles(newFiles),
        suppliers: JSON.stringify(selectedMembers),
        demands: JSON.stringify(_demands),
        priceDetails: JSON.stringify(_priceTypes),
        emailText: senderMsg,
        membersToAdd: JSON.stringify(membersToAdd),
        membersToRemove: JSON.stringify(membersToRemove),
        membersToUpdate: JSON.stringify(membersToUpdate),
        fileAreaBidRequestFiles: fileAreaRoot,
        reminder: reminderPayload ? JSON.stringify(reminderPayload) : undefined,
      }

      const quotationResponse = await ServiceQuotation.sendQuotation(projectSlug, areaSlug, projectQuotation);

      if (shouldPublish !== !!area?.publishedAt) {
        await ServiceProjects.updateProjectAreaIsPublished(currentProject.slug, match.params.areaSlug, shouldPublish);
      }
      ServiceProjects.markQuoteSent(projectSlug, areaSlug, quotationResponse);

      // Delete the draft from DB after a successful submit
      try {
        await ServiceBidRequestDraft.deleteDraft(currentProject._id, area!.areaId._id);
        setDraft(null);
      } catch (e) {
        // Non-fatal: if the delete fails the draft will simply be overwritten on the next save
        console.error('Error deleting bid request draft:', e);
      }

      NotificationManager.success(t('notification.success.submit', 'Skickat'));
      type SupplierNotRestored = { name: string, reason?: 'organizationNumberInUse' | 'adminOnly' };
      const supplierNotRestoredMessage = (supplier: SupplierNotRestored) => {
        switch (supplier.reason) {
          case 'adminOnly':
            return t(
              'notification.warning.submit.supplierNotRestoredAdminOnly',
              'Förfrågan skickades och kontaktuppgifterna sparades, men leverantören {{name}} är fortfarande borttagen eftersom endast administratörer kan lägga till leverantörer i organisationen. Be en administratör återställa den.',
              { name: supplier.name },
            );
          default:
            return t(
              'notification.warning.submit.supplierNotRestored',
              'Förfrågan skickades och kontaktuppgifterna sparades, men leverantören {{name}} kunde inte återställas i leverantörsregistret eftersom en annan aktiv leverantör redan har samma organisationsnummer.',
              { name: supplier.name },
            );
        }
      };
      const suppliersNotRestored: SupplierNotRestored[] = quotationResponse?.suppliersNotRestored ?? [];
      for (const supplier of suppliersNotRestored) {
        NotificationManager.warning(
          supplierNotRestoredMessage(supplier),
          t('notification.warning.submit.title', 'Leverantören är fortfarande borttagen'),
          10000,
        );
      }
      goBack();
    } catch (error) {
      console.log({ error }, "e::::");
      Sentry.captureException(error);
      if (axios.isAxiosError(error)) {
        if (error?.response?.status === 400 && error?.response?.data?.fieldsErrorMessages) {
          const fieldsErrorMessages = error.response.data.fieldsErrorMessages as { [contactId: string]: Omit<Member, "_id"> };
          setSupplierContacts(prevState => {
            const tempContacts = [...prevState];
            for (const [contactId, errorMessages] of Object.entries(fieldsErrorMessages)) {
              const contactIndex = tempContacts.findIndex(contact => contact._id === contactId);
              tempContacts[contactIndex].errorMessages = errorMessages;
            }
            return tempContacts;
          });
          NotificationManager.error(
            t('notification.error.submit.invalidRecipient', 'På grund av en ogiltig mottagare kunde förfrågan inte skickas.'),
            t('notification.error.submit.title', 'Kunde inte skicka')
          );
        } else {
          const message = error.toString();
          const data = error.response?.data;
          if (data?.error == "Error: Field value too long") {
            NotificationManager.error(
              t('notification.error.submit.fieldValueTooLong', 'Innehållet du försöker skicka är för stort. Minska mängden text eller ta bort eventuella inbäddade bilder i beskrivningsfälten.'),
              t('notification.error.submit.title', 'Kunde inte skicka'),
              10000
            );
          } else if (data?.error == "File changed size") {
            console.error(data);
            NotificationManager.error(
              <Trans t={t} i18nKey="notification.error.submit.fileSizeChanged" values={{ fileName: data.expectedName }} />,
              t('notification.error.submit.title', 'Kunde inte skicka'),
              10000
            );
          } else {
            const details = data?.error ?? data?.message ?? data?.toString();
            NotificationManager.error(
              [message, details].filter(x => x).join(": "),
              t('notification.error.submit.title', 'Kunde inte skicka')
            );
            throw error;
          }
        }
      } else {
        NotificationManager.error(
          '' + error,
          t('notification.error.submit.unexpected', 'Oväntat fel'),
        );
        throw error;
      }
    } finally {
      setLoader(false);
    }
  };

  const priceFieldCounts = priceTypes.map(o => o?.label.toLowerCase().trim() ?? "").reduce((map, key) => map.set(key, (map.get(key) ?? 0) + 1), new Map<string, number>());
  const duplicatePriceFields = Array.from(priceFieldCounts.values()).find(count => count > 1);
  const emptyPriceFields = priceFieldCounts.get("") ?? 0;
  const demandsCounts = demands.map(o => o?.label.toLowerCase().trim() ?? "").reduce((map, key) => map.set(key, (map.get(key) ?? 0) + 1), new Map<string, number>());
  const duplicateDemands = Array.from(demandsCounts.values()).find(count => count > 1);
  const emptyDemands = demandsCounts.get("") ?? 0;
  const selectedContacts = supplierContacts.filter(contact => contact.isSelected);
  const hasUnschedulableReminder = reminder.preset !== 'none' && !resolveReminderDay(reminder, expirationDate, new Date());
  const errorMessage = [
    !selectedContacts.length ? t('errorMessage.noSelectedRecipient', 'ingen mottagare vald') : '',
    hasUnschedulableReminder ? t('errorMessage.unschedulableReminder', 'påminnelsen saknar ett datum mellan imorgon och dagen före utgångstiden') : '',
    duplicatePriceFields ? t('errorMessage.duplicatePriceFields', 'samma prisfält finns med flera gånger') : '',
    emptyPriceFields > 0 ? t('errorMessage.emptyPriceFields', 'flera prisfält är tomma', { count: emptyPriceFields }) : '',
    duplicateDemands ? t('errorMessage.duplicateDemands', 'samma skallkrav finns med flera gånger') : '',
    emptyDemands > 0 ? t('errorMessage.emptyDemands', 'flera skallkrav är tomma', { count: emptyDemands }) : '',
    fileAreaUploading ? t('errorMessage.fileUploading', 'en fil laddas upp') : '',
  ].filter(x => x).join(", ");

  const onDraftAccept = () => {
    if (!draft?.data) return;
    setExpirationDate(new Date(draft.data.expirationDate));
    setInfoText(draft.data.infoText);
    setFiles(draft.data.files ?? []);
    setPriceTypeUnits(draft.data.priceTypeUnits);
    setDemands(draft.data.demands);
    setPriceTypes(draft.data.priceTypes);
    setSenderMsg(draft.data.senderMsg);
    setFileScopeQuotationId(draft.data.fileScopeQuotationId);
    setFileScopeQuotationVersion(draft.data.fileScopeQuotationVersion);
    // Restore the FileArea to where the draft's files were uploaded.
    // Discarding keeps the fresh session root (only files copied from the source quotation).
    if (draft.data.fileAreaRoot) setFileAreaRoot(draft.data.fileAreaRoot);
    setReminder(draft.data.reminder ?? { preset: 'none' });
    setSupplierContacts((prevState) => {
      const tempContacts = [...prevState.map(contact => ({ ...contact, isSelected: false }))];

      for (const contact of draft.data.supplierContacts ?? []) {
        const foundContact = tempContacts.find(tempContact => tempContact._id === contact._id);
        if (foundContact) {
          foundContact.isSelected = contact.isSelected;
        }
      }

      return tempContacts;
    });
    setDraftDecision('DRAFT');
    setShowDraftModal(false);
  }

  return (
    <AppLayout title={<BackButton
      onClick={goBack}
      path={[
        projectName,
        t(
          'pageTitle',
          'Förfrågningsunderlag för {{areaName}} version {{version}}',
          {
            areaName,
            context: match.params.version === 'new' ? 'new' : null,
            version: match.params.version !== 'new' && new URLSearchParams(history.location.search).get('draftDecision') === 'use'
              ? String(parseInt(match.params.version) + 1)
              : match.params.version
          }
        )
      ]}
    />}>
      {isQuotationLoaded ? (<>
        <ScrollToTop />
        <div className={styles.container}>
          <div className={styles.content}>
            {suppliers?.length > 0 && renderRecipientsList()}
            {renderMainForm()}
            {renderEmailViewer()}
            {renderReminder()}
            <TooltipContainer
              renderReferenceComponent={(className, ref) =>
                <div
                  ref={ref}
                  style={{ alignSelf: 'flex-end', display: 'flex', gap: '16px' }}
                  className={className}
                >
                  <BtButton
                    loaderShow={publishLoader}
                    onClick={() => isPublished
                      ? handleSubmit(true, setPublishLoader)
                      : setShowPublishModal(true)}
                    disabled={!!errorMessage || notPublishLoader}
                    type="submit"
                    color={isPublished ? 'orange' : 'white'}
                  >
                    {t('button.sendNewVersionAndPublish', 'Skicka ny version och publicera')}
                  </BtButton>
                  {!isPublished && <BtButton
                    loaderShow={notPublishLoader}
                    onClick={() => handleSubmit(false, setNotPublishLoader)}
                    disabled={!!errorMessage || publishLoader}
                    type="submit"
                  >
                    {t('button.sendNewVersion', 'Skicka ny version')}
                  </BtButton>}
                </div>
              }
            >
              {errorMessage && (
                <div style={{ width: 'max-content', maxWidth: '310px' }}>{t('errorMessage.summary', 'Kan inte skicka förfrågan: {{errorMessage}}.', { errorMessage })}</div>
              )}
            </TooltipContainer>
          </div>
        </div>
      </>) : (<div style={{ height: '100%', display: 'grid', placeItems: 'center' }}>
        <Loading type="inline" />
      </div>)}
      <Modal
        title={t('draft.heading', 'Fortsätt från utkast?')}
        buttonInfo={{ label: t('draft.button.continueWithDraft', 'Ja, fortsätt från utkastet'), action: onDraftAccept }}
        cancelButtonText={t('draft.button.startNew', 'Nej, starta ny version')}
        show={showDraftModal}
        setShow={(value) => {
          if (!value) {
            setDraftDecision((oldState) => oldState === 'UNDECIDED' ? 'NEW' : oldState);
          }
          setShowDraftModal(value);
        }}

      >
        <p style={{ color: 'var(--gray-700)' }}>
          {draft?.savedBy
            ? t('draft.infoWithUser', 'Hittade ett sparat utkast från {{savedAt}}, sparat av {{name}}.', { savedAt: moment(draft.updatedAt).locale('sv').format('lll'), name: draft.savedBy.name })
            : t('draft.info', 'Hittade ett sparat utkast från {{savedAt}}.', { savedAt: moment(draft?.updatedAt).locale('sv').format('lll') })
          }
        </p>

        {isOldDraft && <span style={{ fontWeight: '500', color: 'var(--gray-700)' }}>{t('draft.oldDraftWarning', 'Obs! Utkastet är äldre än den senaste versionen.')}</span>}
      </Modal>
      <Modal
        title={tPublish('title', 'Publicera förfrågan')}
        show={showPublishModal}
        setShow={setShowPublishModal}
        buttonInfo={{
          label: t('button.sendNewVersionAndPublish', 'Skicka ny version och publicera'),
          action: () => {
            setShowPublishModal(false);
            handleSubmit(true, setPublishLoader);
          },
        }}
      >
        <div style={{ fontSize: '14px', color: 'var(--gray-700)', maxWidth: '500px' }}>
          <Trans t={tPublish} i18nKey="intro" />
        </div>
      </Modal>
    </AppLayout>
  );
};

export default PageBidRequest;
